import { forwardRef, type InputHTMLAttributes } from 'react'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  description?: string
  error?: string
  indeterminate?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, indeterminate, className = '', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className={`flex items-start gap-3 ${className}`}>
        <div className="flex h-5 items-center">
          <input
            ref={node => {
              if (typeof ref === 'function') {
                ref(node)
              } else if (ref) {
                ref.current = node
              }

              if (node) {
                node.indeterminate = indeterminate || false
              }
            }}
            type="checkbox"
            id={inputId}
            className={`
              h-4 w-4 rounded border transition-colors
              focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900
              disabled:cursor-not-allowed disabled:opacity-50
              ${
                error
                  ? 'border-red-500 text-red-600 focus:ring-red-500 dark:border-red-400'
                  : 'border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-white/20'
              }
            `}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `${inputId}-error` : description ? `${inputId}-description` : undefined}
            {...props}
          />
        </div>
        {(label || description || error) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={inputId}
                className="text-sm font-medium text-gray-700 dark:text-white/65 cursor-pointer select-none"
              >
                {label}
              </label>
            )}
            {description && !error && (
              <p id={`${inputId}-description`} className="text-sm text-gray-500 dark:text-white/45">
                {description}
              </p>
            )}
            {error && (
              <p id={`${inputId}-error`} className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    )
  },
)

Checkbox.displayName = 'Checkbox'
